"use client";

import { usePostHog } from "@posthog/next";
import { usePathname, useSearchParams } from "next/navigation";
import {
  createContext,
  type ReactNode,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CommonRoomScripts, identifyCommonRoomUser } from "./common-room/common-room.browser";
import { useHasTelemetryConsent } from "./consent/use-telemetry-consent";
import {
  captureDatadogRumAction,
  DatadogRumInit,
  DatadogRumRouter,
  identifyDatadogRumUser,
  resetDatadogRumUser,
} from "./datadog/datadog-rum.browser";
import {
  type AnalyticsEventName,
  type AnalyticsEventProperties,
  postHogEventName,
  validateAnalyticsEventProperties,
} from "./event-catalog";
import { ensureFirstTouchCookie } from "./first-touch";
import type { DatadogRumContext, TelemetryProperties, TelemetryUser } from "./telemetry.types";

interface TelemetryContextValue {
  capture: <Name extends AnalyticsEventName>(
    eventName: Name,
    properties?: AnalyticsEventProperties<Name>,
  ) => void;
  identifyByEmail: (email: string) => void;
  identifyUser: (user: TelemetryUser, properties?: TelemetryProperties) => void;
  reset: () => void;
}

const TelemetryContext = createContext<TelemetryContextValue>({
  capture: () => {},
  identifyByEmail: () => {},
  identifyUser: () => {},
  reset: () => {},
});

interface PostHogConsentControls {
  capture: (eventName: string, properties?: Record<string, unknown>, options?: unknown) => void;
  has_opted_in_capturing?: () => boolean;
  has_opted_out_capturing?: () => boolean;
  is_capturing?: () => boolean;
  opt_in_capturing?: () => void;
  opt_out_capturing?: () => void;
  startSessionRecording?: () => void;
  stopSessionRecording?: () => void;
}

export interface TelemetryProviderProps {
  children: ReactNode;
  datadogRumContext?: DatadogRumContext;
}

function TelemetryCaptureProvider({
  children,
  datadogRumContext,
}: {
  children: ReactNode;
  datadogRumContext?: DatadogRumContext;
}) {
  const posthog = usePostHog();
  const hasTelemetryConsent = useHasTelemetryConsent();
  const lastIdentifyFingerprint = useRef<string | null>(null);
  const wasPostHogCaptureEnabled = useRef(false);
  const [isPostHogCaptureEnabled, setIsPostHogCaptureEnabled] = useState(false);

  useEffect(() => {
    ensureFirstTouchCookie(hasTelemetryConsent);
  }, [hasTelemetryConsent]);

  useEffect(() => {
    const consentControls = posthog as typeof posthog & PostHogConsentControls;

    if (hasTelemetryConsent) {
      consentControls.opt_in_capturing?.();
      consentControls.startSessionRecording?.();
      wasPostHogCaptureEnabled.current = true;
      setIsPostHogCaptureEnabled(true);
      return;
    }

    if (wasPostHogCaptureEnabled.current) {
      consentControls.stopSessionRecording?.();
      consentControls.opt_out_capturing?.();
      wasPostHogCaptureEnabled.current = false;
    }

    setIsPostHogCaptureEnabled(false);
  }, [hasTelemetryConsent, posthog]);

  const capture = useCallback(
    <Name extends AnalyticsEventName>(
      eventName: Name,
      properties?: AnalyticsEventProperties<Name>,
    ) => {
      if (!hasTelemetryConsent) return;
      if (!isPostHogCaptureEnabled) return;

      try {
        validateAnalyticsEventProperties(eventName, properties ?? {});
      } catch {
        console.error("Telemetry capture validation failed", { eventName });
        return;
      }
      posthog.capture(postHogEventName(eventName), properties);
      void captureDatadogRumAction(eventName, properties).catch((error: unknown) => {
        console.error("Failed to capture Datadog RUM action", {
          error: error instanceof Error ? error.message : String(error),
        });
      });
    },
    [hasTelemetryConsent, isPostHogCaptureEnabled, posthog],
  );

  const identifyUser = useCallback(
    (user: TelemetryUser, properties?: TelemetryProperties) => {
      if (!hasTelemetryConsent) return;
      if (!isPostHogCaptureEnabled) return;

      const identifyFingerprint = JSON.stringify({
        user,
        properties,
      });
      if (lastIdentifyFingerprint.current === identifyFingerprint) return;
      lastIdentifyFingerprint.current = identifyFingerprint;

      const posthogWithIdentity = posthog as typeof posthog & {
        _isIdentified?: () => boolean;
      };
      const isIdentified = posthogWithIdentity._isIdentified?.() ?? false;
      const distinctId = posthog.get_distinct_id();

      if (!isIdentified || distinctId !== user.id) {
        posthog.identify(user.id, {
          ...properties,
          clerkId: user.id,
          email: user.email ?? undefined,
          firstName: user.firstName ?? undefined,
          lastName: user.lastName ?? undefined,
          name: user.fullName ?? undefined,
          createdAt: user.createdAt ?? undefined,
          organizationId: user.organization?.id,
          organizationName: user.organization?.name ?? undefined,
          organizationSlug: user.organization?.slug ?? undefined,
          organizationRole: user.organization?.role ?? undefined,
        });
      }

      identifyCommonRoomUser(user);
      void identifyDatadogRumUser(user, properties).catch((error: unknown) => {
        console.error("Failed to identify Datadog RUM user", {
          error: error instanceof Error ? error.message : String(error),
        });
      });
    },
    [hasTelemetryConsent, isPostHogCaptureEnabled, posthog],
  );

  const identifyByEmail = useCallback(
    (email: string) => {
      if (!hasTelemetryConsent) return;
      if (!isPostHogCaptureEnabled) return;
      if (!email) return;

      const posthogWithIdentity = posthog as typeof posthog & {
        _isIdentified?: () => boolean;
      };
      const isIdentified = posthogWithIdentity._isIdentified?.() ?? false;
      const distinctId = posthog.get_distinct_id();

      if (!isIdentified || distinctId !== email) {
        posthog.identify(email, { email });
      }
    },
    [hasTelemetryConsent, isPostHogCaptureEnabled, posthog],
  );

  const reset = useCallback(() => {
    lastIdentifyFingerprint.current = null;
    posthog.reset();
    void resetDatadogRumUser().catch((error: unknown) => {
      console.error("Failed to reset Datadog RUM user", {
        error: error instanceof Error ? error.message : String(error),
      });
    });
  }, [posthog]);

  const contextValue = useMemo<TelemetryContextValue>(
    () => ({ capture, identifyByEmail, identifyUser, reset }),
    [capture, identifyByEmail, identifyUser, reset],
  );

  return (
    <TelemetryContext.Provider value={contextValue}>
      {children}
      <FrontendTelemetryScripts
        datadogRumContext={datadogRumContext}
        isPostHogCaptureEnabled={isPostHogCaptureEnabled}
      />
    </TelemetryContext.Provider>
  );
}

export function TelemetryProvider({ children, datadogRumContext }: TelemetryProviderProps) {
  return (
    <TelemetryCaptureProvider datadogRumContext={datadogRumContext}>
      {children}
    </TelemetryCaptureProvider>
  );
}

function FrontendTelemetryScripts({
  datadogRumContext,
  isPostHogCaptureEnabled,
}: {
  datadogRumContext?: DatadogRumContext;
  isPostHogCaptureEnabled: boolean;
}) {
  const hasTelemetryConsent = useHasTelemetryConsent();

  return (
    <>
      <DatadogRumInit context={datadogRumContext} hasConsent={hasTelemetryConsent} />
      <DatadogRumRouter />
      {hasTelemetryConsent && isPostHogCaptureEnabled ? (
        <>
          <CommonRoomScripts />
          <Suspense fallback={null}>
            <PostHogPageViewTracker />
          </Suspense>
        </>
      ) : null}
    </>
  );
}

function PostHogPageViewTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const posthog = usePostHog() as ReturnType<typeof usePostHog> & PostHogConsentControls;

  useEffect(() => {
    const search = searchParams.toString();
    const currentUrl = new URL(search ? `${pathname}?${search}` : pathname, window.location.origin);

    posthog.capture("$pageview", { $current_url: currentUrl.toString() }, { send_instantly: true });
  }, [pathname, posthog, searchParams]);

  return null;
}

export function useTelemetryCaptureEvent() {
  return useContext(TelemetryContext).capture;
}

export function useTelemetryIdentifyUser() {
  return useContext(TelemetryContext).identifyUser;
}

export function useTelemetryIdentifyByEmail() {
  return useContext(TelemetryContext).identifyByEmail;
}

export function useTelemetryReset() {
  return useContext(TelemetryContext).reset;
}

export { useHasTelemetryConsent };
